/*
 * Duplicated from @confluence/create-blank-fabric-page to avoid a circular dependency —
 * this experiment package must be a dependency of create-blank-fabric-page, so we can't
 * import from it. If this experiment wins, we should revisit the package structure.
 */
import type { RoutesContextType } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { CREATE_CONTENT } from '@confluence/named-routes/entry-points/routes.ts';
import { INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM } from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';

export enum ConfluencePageSubType {
	LIVE = 'LIVE',
}

const queryParamKeys = {
	subType: 'subType',
	spaceKey: 'spaceKey',
	parentPageId: 'parentPageId',
	source: 'source',
	withFallback: 'withFallback',
	insertTemplateWithKey: INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM,
	templateKey: 'templateKey',
	/* Created By Flow Analytic contributes to Core Actions/FO and should sometimes be disabled
	(ex: user going through onboarding may be firing this event during onboarding screens and should not contribute to KR metric)*/
	disableCreatedByFlowAnalytic: 'dcbfa',
};

const SUPPORTED_CONTENT_TYPES = ['page', 'whiteboard'] as const;
export type CreateRouteSupportedContentType = (typeof SUPPORTED_CONTENT_TYPES)[number];

type CreatePage = {
	contentType: 'page';
	subType?: ConfluencePageSubType;
	source: string;
	/**
	 * Parent page of created page
	 */
	parentPageId?: string;
	/**
	 * Space key of target space to create page in
	 */
	spaceKey?: string;
	withFallback?: true;
	/** Template key for creating page with a specific template */
	templateKey?: string;
	disableCreatedByFlowAnalytic?: boolean;
};
export type CreateWhiteboard = {
	contentType: 'whiteboard';
	source: string;
	spaceKey?: string;
	parentPageId?: string;
	withFallback?: true;
	insertTemplateWithKey?: string;
	disableCreatedByFlowAnalytic?: boolean;
};

type CreateContentOptions = CreatePage | CreateWhiteboard;

export const buildCreationLink = (options: CreateContentOptions): string => {
	const queryParams: Record<string, any> = {};
	if ('subType' in options && options.subType) {
		queryParams[queryParamKeys.subType] = options.subType;
	}
	if ('spaceKey' in options && options.spaceKey) {
		queryParams[queryParamKeys.spaceKey] = options.spaceKey;
	}
	if ('parentPageId' in options && options.parentPageId) {
		queryParams[queryParamKeys.parentPageId] = options.parentPageId;
	}
	if ('withFallback' in options && options.withFallback) {
		queryParams[queryParamKeys.withFallback] = options.withFallback;
	}
	if ('source' in options && options.source) {
		queryParams[queryParamKeys.source] = options.source;
	}
	if (INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM in options && options.insertTemplateWithKey) {
		queryParams[queryParamKeys.insertTemplateWithKey] = options.insertTemplateWithKey;
	}
	if ('templateKey' in options && options.templateKey) {
		queryParams[queryParamKeys.templateKey] = options.templateKey;
	}
	if ('disableCreatedByFlowAnalytic' in options && options.disableCreatedByFlowAnalytic) {
		queryParams[queryParamKeys.disableCreatedByFlowAnalytic] = options.disableCreatedByFlowAnalytic;
	}

	return CREATE_CONTENT.toUrl({ contentType: options.contentType }, { query: queryParams });
};

type ParseQueryParamsResponse = {
	subType?: string;
	spaceKey?: string;
	withFallback?: true;
	source?: string;
	parentPageId?: string;
	insertTemplateWithKey?: string;
	templateKey?: string;
	disableCreatedByFlowAnalytic?: boolean;
};

type QueryParams = ReturnType<RoutesContextType['getQueryParams']>;

export const parseQueryParams = (queryParams: QueryParams): ParseQueryParamsResponse => {
	const subType = getSingleParam(queryParams, queryParamKeys.subType);
	const spaceKey = getSingleParam(queryParams, queryParamKeys.spaceKey);
	const parentPageId = getSingleParam(queryParams, queryParamKeys.parentPageId);
	const withFallback = getSingleParam(queryParams, queryParamKeys.withFallback) ? true : undefined;
	const source = getSingleParam(queryParams, queryParamKeys.source);
	const insertTemplateWithKey =
		getSingleParam(queryParams, queryParamKeys.insertTemplateWithKey) ?? undefined;
	const templateKey = getSingleParam(queryParams, queryParamKeys.templateKey);
	const disableCreatedByFlowAnalytic =
		getSingleParam(queryParams, queryParamKeys.disableCreatedByFlowAnalytic) === 'true';

	return {
		subType,
		spaceKey,
		parentPageId,
		withFallback,
		source,
		insertTemplateWithKey,
		templateKey,
		disableCreatedByFlowAnalytic,
	};
};
